import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useFetchPensionPlans, useCreatePensionPlan, useUpdatePensionPlan, useDeletePensionPlan } from '@/hooks/pension/usePensionPlans.ts';
import { useInvestmentTypes } from '@/hooks/useInvestmentTypes';
import { useFetchAccounts } from '@accountHooks';
import { useInvestmentCurrencyConversion } from '@/hooks/useInvestmentCurrencyConversion';
import { useCreateInvestmentTransaction } from '@/hooks/useInvestmentTransactions';
import { useMemo, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  PortfolioSummarySkeleton, 
  DistributionChartSkeleton,
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowDownToLine, ArrowUpFromLine, Plus, AlertCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { TypeSummaryCard } from '@/components/investment-summary/TypeSummaryCard';
import { InvestmentDistributionChart } from '@/components/investment-charts/InvestmentDistributionChart';
import { PensionPlanCard } from '@/components/pension-plan-card/PensionPlanCard';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { PensionPlanForm } from '@/components/pension-plan-form/PensionPlanForm';
import { InvestmentTransactionForm } from '@/components/investment-transaction-form/InvestmentTransactionForm';
import type { InvestmentTransactionFormData } from '@/components/investment-transaction-form/InvestmentTransactionForm';
import {PensionPlanAccount} from "@/integrations/supabase/pension.models.ts";

/**
 * PensionPlansPage component displays and manages Pension Plan investments.
 * This is a dedicated page for Pension Plans accessible at /investments/pension-plans.
 * Supports three pension types: Government, Occupational, and Individual.
 */
export default function PensionPlansPage() {
  // Dialog state
  const [pensionFormOpen, setPensionFormOpen] = useState(false);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [editingPensionPlan, setEditingPensionPlan] = useState<PensionPlanAccount | null>(null);
  const [deletingPensionPlanId, setDeletingPensionPlanId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all pension plans
  const { 
    data: pensionPlans = [], 
    isLoading: pensionPlansLoading, 
    error: pensionPlansError 
  } = useFetchPensionPlans();

  // Fetch investment types to get pension plan type IDs
  const { 
    data: investmentTypes = [], 
    isLoading: typesLoading, 
    error: typesError 
  } = useInvestmentTypes();

  // Fetch accounts for transaction form
  const { 
    data: accounts = [], 
    isLoading: accountsLoading 
  } = useFetchAccounts();

  // Mutation hooks
  const createPensionPlan = useCreatePensionPlan();
  const updatePensionPlan = useUpdatePensionPlan();
  const deletePensionPlan = useDeletePensionPlan();
  const createInvestmentTransaction = useCreateInvestmentTransaction();

  // Get pension plan type IDs
  const pensionPlanTypeIds = useMemo(() => {
    return investmentTypes
      .filter(type => 
        type.name === 'Government Pension Plan' || 
        type.name === 'Occupational Pension Plan' || 
        type.name === 'Individual Pension Plan' ||
        type.name === 'Pension Plan'
      )
      .map(type => type.id);
  }, [investmentTypes]);

  // Get base currency from first pension plan or default to USD
  const baseCurrency = useMemo(() => {
    return pensionPlans[0]?.currency || 'USD';
  }, [pensionPlans]);

  const filteredPensionPlans = useMemo(() => {
    if (!searchQuery) return pensionPlans;
    const lowerQuery = searchQuery.toLowerCase();
    return pensionPlans.filter(inv => 
      inv.fund_name?.toLowerCase().includes(lowerQuery) ||
      inv.fund_manager?.toLowerCase().includes(lowerQuery) ||
      inv.account_number?.toLowerCase().includes(lowerQuery) ||
      inv.member_number?.toLowerCase().includes(lowerQuery)
    );
  }, [pensionPlans, searchQuery]);

  // Currency conversion hook
  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedInvestments,
    totalNetWorth,
    isConverting,
    conversionError,
  } = useInvestmentCurrencyConversion({
    baseCurrency,
    investments: filteredPensionPlans,
  });

  // Handle pension plan data loading errors
  useEffect(() => {
    if (pensionPlansError) {
      toast.error('Failed to load pension plans. Please try again.');
    }
  }, [pensionPlansError]);

  // Handle investment types loading errors
  useEffect(() => {
    if (typesError) {
      toast.error('Failed to load investment types. Please try again.');
    }
  }, [typesError]);

  // Combined loading state
  const isLoading = pensionPlansLoading || typesLoading || accountsLoading;

  // Handler functions for action buttons
  const handleAddPensionPlan = () => {
    setEditingPensionPlan(null);
    setPensionFormOpen(true);
  };

  const handleDeposit = () => {
    setTransactionType('deposit');
    setTransactionFormOpen(true);
  };

  const handleWithdraw = () => {
    setTransactionType('withdrawal');
    setTransactionFormOpen(true);
  };

  const handleEditPensionPlan = (pensionPlan: PensionPlanAccount) => {
    setEditingPensionPlan(pensionPlan);
    setPensionFormOpen(true);
  };

  const handleDeletePensionPlan = (pensionPlanId: string) => {
    setDeletingPensionPlanId(pensionPlanId);
  };

  // Handler for saving pension plan (create or update)
  const handleSavePensionPlan = async (data: PensionPlanFormData) => {
    try {
      if (editingPensionPlan) {
        // Update existing pension plan
        await updatePensionPlan.mutateAsync({
          id: editingPensionPlan.id,
          ...data,
        });
        toast.success('Pension plan updated successfully');
      } else {
        // Create new pension plan
        await createPensionPlan.mutateAsync(data);
        toast.success('Pension plan created successfully');
      }
      setPensionFormOpen(false);
      setEditingPensionPlan(null);
    } catch (error) {
      toast.error(editingPensionPlan ? 'Failed to update pension plan' : 'Failed to create pension plan');
    }
  };

  // Handler for canceling pension plan form
  const handleCancelPensionForm = () => {
    setPensionFormOpen(false);
    setEditingPensionPlan(null);
  };

  // Handler for saving transaction (deposit or withdrawal)
  const handleSaveTransaction = async (data: InvestmentTransactionFormData) => {
    try {
      // Form validation ensures all required fields are present
      await createInvestmentTransaction.mutateAsync(data);
      toast.success(`${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`);
      setTransactionFormOpen(false);
    } catch (error) {
      toast.error(`Failed to record ${data.transaction_type}`);
    }
  };

  // Handler for canceling transaction form
  const handleCancelTransactionForm = () => {
    setTransactionFormOpen(false);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!deletingPensionPlanId) return;
    
    try {
      await deletePensionPlan.mutateAsync(deletingPensionPlanId);
      toast.success('Pension plan deleted successfully');
      setDeletingPensionPlanId(null);
    } catch (error) {
      toast.error('Failed to delete pension plan');
    }
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setDeletingPensionPlanId(null);
  };

  // Disable deposit/withdraw buttons when no pension plans exist
  const hasPensionPlans = pensionPlans.length > 0;

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          // Reset state and refetch data when error boundary resets
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
            <PortfolioSummarySkeleton />
            <DistributionChartSkeleton />
          </div>
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      onReset={() => {
        // Reset state and refetch data when error boundary resets
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Pension Plans</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Manage your pension plan investments
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto" role="group" aria-label="Pension Plans actions">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search funds..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <CurrencyToggle
                baseCurrency={baseCurrency}
                selectedCurrency={selectedCurrency}
                onCurrencyChange={setSelectedCurrency}
                balance={totalNetWorth}
                showBalance={false}
              />
              <Button
                onClick={handleDeposit}
                disabled={!hasPensionPlans}
                variant="outline"
                aria-label="Deposit funds to Pension Plan"
                aria-disabled={!hasPensionPlans}
                className="min-h-[44px]"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Deposit
              </Button>
              <Button
                onClick={handleWithdraw}
                disabled={!hasPensionPlans}
                variant="outline"
                aria-label="Withdraw funds from Pension Plan"
                aria-disabled={!hasPensionPlans}
                className="min-h-[44px]"
              >
                <ArrowUpFromLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Withdraw
              </Button>
              <Button
                onClick={handleAddPensionPlan}
                aria-label="Add new Pension Plan investment"
                className="min-h-[44px]"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Currency Conversion Loading Indicator */}
        {isConverting && (
          <Alert role="status" aria-live="polite" aria-busy="true">
            <AlertDescription>
              Converting to {selectedCurrency}...
            </AlertDescription>
          </Alert>
        )}

        {/* Currency Conversion Error */}
        {conversionError && (
          <Alert variant="destructive" role="alert" aria-live="assertive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              Failed to convert currencies. Displaying amounts in original currencies.
            </AlertDescription>
          </Alert>
        )}

        {/* Summary Cards and Distribution Chart */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          <TypeSummaryCard
            typeName='Pension Plans'
            typeId={pensionPlanTypeIds[0] || ''}
            investments={filteredPensionPlans}
            currency={selectedCurrency}
            isLoading={isLoading}
            filterByType={false}
          />
          <InvestmentDistributionChart
            investments={convertedInvestments}
            currency={selectedCurrency}
            isLoading={isLoading}
          />
        </div>

        {/* Pension Plan Cards List or Empty State */}
        {hasPensionPlans ? (
          <div 
            className="space-y-4" 
            role="list" 
            aria-label="Pension Plan investments"
            aria-live="polite"
          >
            {convertedInvestments.map((pensionPlan) => (
              <PensionPlanCard
                key={pensionPlan.id}
                pensionPlan={pensionPlan as PensionPlanAccount & { convertedBalance?: number }}
                currency={selectedCurrency}
                onEdit={() => handleEditPensionPlan(pensionPlan)}
                onDelete={() => handleDeletePensionPlan(pensionPlan.id)}
              />
            ))}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-base sm:text-lg text-muted-foreground mb-4">
                No Pension Plan investments yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Get started by adding your first Pension Plan investment
              </p>
              <Button 
                onClick={handleAddPensionPlan} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add your first Pension Plan investment"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Investment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Pension Plan Dialog */}
        <Dialog open={pensionFormOpen} onOpenChange={setPensionFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="pension-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {editingPensionPlan ? 'Edit Pension Plan' : 'Add Pension Plan Investment'}
              </DialogTitle>
              <DialogDescription id="pension-dialog-description">
                {editingPensionPlan 
                  ? 'Update the details of your Pension Plan investment.' 
                  : 'Add a new Pension Plan investment to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <PensionPlanForm
              pensionPlan={editingPensionPlan}
              onSave={handleSavePensionPlan}
              onCancel={handleCancelPensionForm}
              isSubmitting={createPensionPlan.isPending || updatePensionPlan.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Deposit/Withdraw Transaction Dialog */}
        <Dialog open={transactionFormOpen} onOpenChange={setTransactionFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="transaction-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
              </DialogTitle>
              <DialogDescription id="transaction-dialog-description">
                {transactionType === 'deposit' 
                  ? 'Add funds to your Pension Plan investment.' 
                  : 'Withdraw funds from your Pension Plan investment.'}
              </DialogDescription>
            </DialogHeader>
            <InvestmentTransactionForm
              transactionType={transactionType}
              investments={pensionPlans}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={handleCancelTransactionForm}
              isSubmitting={createInvestmentTransaction.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingPensionPlanId} onOpenChange={(open) => !open && handleCancelDelete()}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the pension plan account
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of pension plan"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
